import { useState, useRef, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { PaperPlaneTilt as Send, X, Envelope as Mail, ChatCircle as MessageCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getStoredEmail } from "../hooks/useUserState";
import useUserState from "../hooks/useUserState";
import { RelayLoader } from "./RelayLoader";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "#FAF7F0",
  ink: "#2A1E5C",
  ink2: "#5D5A6B",
  muted: "#8B7E6A",
  line: "#E8E2D5",
  card: "#FFFFFF",
  gold: "#F5CB5C",
  bubbleUser: "#2A1E5C",
  bubbleCoach: "#FFF3C6",
};

const SUGGESTIONS = [
  "How do I set up my Legend?",
  "What's a Payday Rhythm?",
  "Can I get a refund?",
];

// Routes where we hide the bubble (already have Coach access or shouldn't distract)
const HIDE_ON = ["/support", "/start/", "/checkout"];

// Lightweight renderer: turns **bold** and [text](url) into JSX (links open in a new tab).
function renderRich(text) {
  const nodes = [];
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0, m, key = 0;
  const pushBold = (chunk) => {
    const parts = chunk.split(/\*\*([^*]+)\*\*/g);
    parts.forEach((p, idx) => {
      if (!p) return;
      nodes.push(idx % 2 === 1
        ? <strong key={`b${key++}`}>{p}</strong>
        : <span key={`t${key++}`}>{p}</span>);
    });
  };
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) pushBold(text.slice(last, m.index));
    nodes.push(
      <a key={`l${key++}`} href={m[2]} target="_blank" rel="noopener noreferrer"
         className="underline font-semibold" style={{ color: "#2A1E5C" }}>
        {m[1]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) pushBold(text.slice(last));
  return nodes;
}

export default function CoachBubble() {
  const { pathname } = useLocation();
  const { state } = useUserState();
  // Members get full coaching; everyone else chats in "public mode" (product/pricing/philosophy only).
  const isPayingCustomer = !!(state && (
    state.has_payday_relay || state.has_four_relay || state.has_income_map_purchase || state.has_pit_crew
  ));
  const greeting = {
    role: "assistant",
    content: isPayingCustomer
      ? "Hey, I'm Caddy, your Payday Relay spotter. Ask me anything, setup, weekly relays, or reading your scoreboard. What's on your mind?"
      : "Hey, I'm Caddy 🏁. Your Payday Relay spotter. Ask me how it works, the pricing, or the idea behind it. Got a feature idea? Tap \u201cMake a suggestion\u201d below.",
  };
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("paydayrelay.coachHidden") === "1"; } catch { return false; }
  });
  const [pulse, setPulse] = useState(false);
  const [messages, setMessages] = useState([greeting]);
  const [mode, setMode] = useState("chat"); // 'chat' | 'suggest'
  const [suggestText, setSuggestText] = useState("");
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestDone, setSuggestDone] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showEscalate, setShowEscalate] = useState(false);
  const [escalateEmail, setEscalateEmail] = useState(() => getStoredEmail() || "");
  const [escalateNote, setEscalateNote] = useState("");
  const [escalating, setEscalating] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [nudges, setNudges] = useState([]);
  const [usedProgress, setUsedProgress] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, open]);

  // First-ever visit: pulse the bubble for ~3s so people notice it, then never again.
  useEffect(() => {
    try {
      const shown = localStorage.getItem("paydayrelay.coachPulseShown");
      if (!shown) {
        // Small delay so the pulse starts after the page settles
        const t1 = setTimeout(() => setPulse(true), 1200);
        const t2 = setTimeout(() => {
          setPulse(false);
          try { localStorage.setItem("paydayrelay.coachPulseShown", "1"); } catch { /* noop */ }
        }, 4500);
        return () => { clearTimeout(t1); clearTimeout(t2); };
      }
    } catch { /* noop */ }
  }, []);

  // Let the "Ask Caddy" item in the More menu bring the bubble back and open it.
  useEffect(() => {
    const show = () => {
      try { localStorage.removeItem("paydayrelay.coachHidden"); } catch { /* noop */ }
      setHidden(false);
      setOpen(true);
    };
    window.addEventListener("caddy:show-bubble", show);
    return () => window.removeEventListener("caddy:show-bubble", show);
  }, []);

  // Quiet, rules-based invitations (app-side, no AI, no pop-up). Only for signed-in
  // members inside the app, and only if they haven't turned nudges off.
  useEffect(() => {
    if (!pathname.startsWith("/app") || !isPayingCustomer) return;
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/nudges`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancel && d.enabled) setNudges(d.nudges || []);
      } catch { /* noop */ }
    })();
    return () => { cancel = true; };
  }, [pathname, isPayingCustomer]);

  // Hide bubble on excluded routes
  if (HIDE_ON.some((p) => pathname.startsWith(p))) return null;

  const isApp = pathname.startsWith("/app");
  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem("paydayrelay.coachHidden", "1"); } catch { /* noop */ }
    setHidden(true);
  };
  // In-app only: once dismissed, stay gone until re-opened from More → Ask Caddy.
  if (hidden && isApp && !open) return null;

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);
    try {
      const res = await fetch(`${API}/api/support/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed, is_member: isPayingCustomer }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Caddy hit a snag, try again.");
        setMessages((m) => m.slice(0, -1));
        return;
      }
      setSessionId(data.session_id);
      if (data.used_progress) setUsedProgress(true);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      toast.error("Network hiccup, try again.");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const NUDGE_STARTER = {
    first_relay_complete: "What does a strong next relay look like?",
    target_hit: "How's my week looking?",
    pending_overdue: "What should I do about my pending payment?",
  };

  const dismissNudge = async (key, e) => {
    if (e) e.stopPropagation();
    setNudges((n) => n.filter((x) => x.key !== key));
    try {
      await fetch(`${API}/api/caddy/nudges/dismiss`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key }),
      });
    } catch { /* noop */ }
  };

  const openFromNudge = (n) => {
    setOpen(true);
    setPulse(false);
    setInput(NUDGE_STARTER[n.type] || "How's my week looking?");
  };

  const submitSuggestion = async () => {
    const text = suggestText.trim();
    if (!text || suggestBusy) return;
    setSuggestBusy(true);
    try {
      const res = await fetch(`${API}/api/support/suggestion`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, email: getStoredEmail() || null, session_id: sessionId }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't send that, try again."); return; }
      setSuggestDone(true);
      setSuggestText("");
    } catch { toast.error("Network hiccup, try again."); }
    finally { setSuggestBusy(false); }
  };

  const escalate = async () => {
    if (!sessionId) { toast.error("Ask Caddy at least one question first."); return; }
    setEscalating(true);
    try {
      const res = await fetch(`${API}/api/support/escalate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, email: escalateEmail || null, note: escalateNote || null }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Couldn't send."); return; }
      setEscalated(true);
      toast.success("Sent to support, we'll get back to you soon.");
    } catch { toast.error("Network hiccup, try again."); }
    finally { setEscalating(false); }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-2">
          {isApp && nudges.length > 0 && (
            <div
              className="relative max-w-[260px] rounded-2xl shadow-lg px-3.5 py-3 pr-8 cursor-pointer transition-transform hover:-translate-y-0.5"
              style={{ background: COLOR.card, border: `1px solid ${COLOR.line}`, boxShadow: "0 8px 24px rgba(42,30,92,0.18)" }}
              onClick={() => openFromNudge(nudges[0])}
              data-testid="coach-nudge-card"
            >
              <button
                onClick={(e) => dismissNudge(nudges[0].key, e)}
                className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center hover:bg-black/5"
                data-testid="coach-nudge-dismiss"
                aria-label="Dismiss"
              >
                <X className="w-3 h-3" style={{ color: COLOR.muted }} />
              </button>
              <p className="text-xs font-bold leading-snug" style={{ color: COLOR.ink }}>{nudges[0].title}</p>
              <p className="text-[11px] leading-snug mt-0.5" style={{ color: COLOR.ink2 }}>{nudges[0].invite}</p>
              <span className="text-[11px] font-semibold mt-1 inline-block" style={{ color: COLOR.ink }}>Open Caddy →</span>
            </div>
          )}
          <div className="relative flex items-center">
            <button
              onClick={() => { setOpen(true); setPulse(false); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-full font-semibold text-sm shadow-lg transition-transform hover:scale-105 ${pulse ? "cb-pulse" : ""}`}
              style={{ background: COLOR.gold, color: COLOR.ink, boxShadow: "0 8px 24px rgba(42,30,92,0.25)" }}
              data-testid="coach-bubble-trigger"
              aria-label="Ask Caddy"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="hidden sm:inline">Ask Caddy 🏁</span>
              {isApp && nudges.length > 0 && (
                <span
                  className="absolute -top-1 left-1 w-2.5 h-2.5 rounded-full"
                  style={{ background: "#E0574A", border: "2px solid #FAF7F0" }}
                  data-testid="coach-nudge-dot"
                />
              )}
            </button>
            {isApp && (
              <button
                onClick={dismiss}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow"
                style={{ background: COLOR.ink, color: "#fff" }}
                data-testid="coach-bubble-dismiss"
                aria-label="Hide Caddy (find it again in More)"
                title="Hide Caddy — reopen from More → Ask Caddy"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed z-50 flex flex-col shadow-2xl overflow-hidden"
          style={{
            background: COLOR.card,
            bottom: "20px",
            right: "20px",
            left: "20px",
            top: "20px",
            maxWidth: "420px",
            maxHeight: "620px",
            marginLeft: "auto",
            borderRadius: "18px",
            border: `1px solid ${COLOR.line}`,
          }}
          data-testid="coach-bubble-panel"
        >
          {/* Panel header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: COLOR.line, background: COLOR.bg }}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">🏁</span>
              <div>
                <p className="text-sm font-bold" style={{ color: COLOR.ink }}>Ask Caddy</p>
                {isPayingCustomer && (
                  <p className="text-[10px]" style={{ color: COLOR.muted }}>Knows the Payday Relay system inside out</p>
                )}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-full hover:bg-black/5"
              data-testid="coach-bubble-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" style={{ color: COLOR.ink2 }} />
            </button>
          </div>

          {mode === "suggest" ? (
            <div className="flex-1 overflow-y-auto px-5 py-6 flex flex-col" data-testid="coach-suggest">
              {suggestDone ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                  <p className="text-base font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Thank you, idea received!</p>
                  <p className="text-sm leading-relaxed mb-6" style={{ color: COLOR.ink2 }}>
                    I've passed your suggestion straight to the Payday Relay team. Every idea helps shape what we build next.
                  </p>
                  <button onClick={() => { setMode("chat"); setSuggestDone(false); }} className="px-6 py-2.5 rounded-full font-semibold text-sm" style={{ background: COLOR.ink, color: "#fff" }} data-testid="coach-suggest-back">
                    Back to chat
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-base font-bold mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Make a suggestion</p>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: COLOR.ink2 }}>
                    Got a feature idea or feedback? Tell me and I'll pass it straight to the team.
                  </p>
                  <textarea
                    value={suggestText}
                    onChange={(e) => setSuggestText(e.target.value)}
                    placeholder="I'd love it if Payday Relay could…"
                    rows={6}
                    maxLength={4000}
                    className="w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none mb-4 resize-none"
                    style={{ borderColor: COLOR.line, background: COLOR.bg, color: COLOR.ink }}
                    data-testid="coach-suggest-input"
                  />
                  <div className="flex gap-2 mt-auto">
                    <button onClick={() => setMode("chat")} className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm border" style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "#fff" }} data-testid="coach-suggest-cancel">
                      Never mind
                    </button>
                    <button onClick={submitSuggestion} disabled={suggestBusy || !suggestText.trim()} className="flex-1 px-4 py-2.5 rounded-full font-bold text-sm disabled:opacity-50" style={{ background: "var(--pr-ink-surface)", color: "#fff" }} data-testid="coach-suggest-submit">
                      {suggestBusy ? "Sending…" : "Send idea"}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
          <>
          {/* Feed */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {usedProgress && (
              <div
                className="rounded-xl px-3 py-2 text-[11px] leading-snug"
                style={{ background: "#FFF8E6", border: `1px solid ${COLOR.line}`, color: COLOR.ink2 }}
                data-testid="coach-progress-notice"
              >
                Caddy used the relay numbers you've logged in Payday Relay (target, earned, received, days left) to answer — it never reads your Google Sheet.{" "}
                <Link to="/app/settings" className="font-semibold underline" style={{ color: COLOR.ink }}>Manage in Settings</Link>.
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : i === 0 ? "justify-center" : "justify-start"}`}
                data-testid={`bubble-msg-${m.role}-${i}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${i === 0 ? "text-center" : ""}`}
                  style={{
                    background: m.role === "user" ? COLOR.bubbleUser : COLOR.bubbleCoach,
                    color: m.role === "user" ? "#fff" : COLOR.ink,
                    borderBottomRightRadius: m.role === "user" ? "6px" : "16px",
                    borderBottomLeftRadius: m.role === "user" ? "16px" : "6px",
                  }}
                >
                  {m.role === "assistant" ? renderRich(m.content) : m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl inline-flex items-center" style={{ background: COLOR.bubbleCoach }} data-testid="coach-bubble-typing">
                  <RelayLoader label={null} size="sm" />
                </div>
              </div>
            )}
            {messages.length === 1 && !sending && (
              <div className="flex flex-wrap gap-1.5 pt-2 justify-center">
                {SUGGESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="px-2.5 py-1.5 rounded-full text-[11px] font-medium border"
                    style={{ borderColor: COLOR.line, background: COLOR.card, color: COLOR.ink2 }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t px-3 py-3 flex items-center gap-2"
            style={{ borderColor: COLOR.line }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Caddy…"
              maxLength={2000}
              disabled={sending}
              className="flex-1 px-3 py-2 rounded-full border text-sm focus:outline-none"
              style={{ borderColor: COLOR.line, background: COLOR.bg, color: COLOR.ink }}
              data-testid="coach-bubble-input"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="p-2.5 rounded-full disabled:opacity-40"
              style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
              data-testid="coach-bubble-send"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
          <div className="flex items-center justify-center gap-3 pb-2 flex-wrap">
            <button
              onClick={() => { setMode("suggest"); setSuggestDone(false); }}
              className="text-[11px] underline"
              style={{ color: COLOR.muted }}
              data-testid="coach-bubble-suggest-btn"
            >
              Make a suggestion
            </button>
            {isPayingCustomer ? (
              <button
                onClick={() => setShowEscalate(true)}
                disabled={!sessionId}
                className="text-[11px] underline disabled:opacity-40"
                style={{ color: COLOR.muted }}
                data-testid="coach-bubble-escalate"
              >
                Contact support
              </button>
            ) : (
              <Link
                to="/start/free-sprint"
                onClick={() => setOpen(false)}
                className="text-[11px] underline"
                style={{ color: COLOR.muted }}
                data-testid="coach-signup-link"
              >
                Start my Free Sprint →
              </Link>
            )}
          </div>
          <p
            className="px-4 pb-3 text-[10px] leading-snug text-center"
            style={{ color: COLOR.muted, borderTop: `1px solid ${COLOR.line}`, paddingTop: "8px" }}
            data-testid="coach-disclaimer"
          >
            Caddy provides informational guidance based on the Payday Relay method. It does not provide financial or tax advice. Always verify your numbers with the Relay Calculator.
          </p>
          </>
          )}
        </div>
      )}

      {/* Escalate modal */}
      {showEscalate && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ background: "rgba(42, 30, 92, 0.55)" }}
          onClick={() => !escalating && setShowEscalate(false)}
        >
          <div className="max-w-md w-full rounded-2xl p-6" style={{ background: COLOR.card }} onClick={(e) => e.stopPropagation()}>
            {escalated ? (
              <div className="text-center py-4">
                <h3 className="text-xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Sent to support</h3>
                <p className="text-sm" style={{ color: COLOR.ink2 }}>We'll follow up at {escalateEmail} as soon as we can. Keep running your relay 🏁</p>
                <button
                  onClick={() => { setShowEscalate(false); setEscalated(false); }}
                  className="mt-5 px-6 py-2 rounded-full font-semibold text-sm"
                  style={{ background: COLOR.ink, color: "#fff" }}
                >Back to chat</button>
              </div>
            ) : (
              <>
                <h3 className="text-xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>Send this to support</h3>
                <p className="text-sm mb-5" style={{ color: COLOR.ink2 }}>I'll forward the whole conversation. Add your email so we can reply.</p>
                <label className="block text-xs font-semibold mb-1" style={{ color: COLOR.ink }}>Your email</label>
                <input type="email" value={escalateEmail} onChange={(e) => setEscalateEmail(e.target.value)} placeholder="you@example.com" className="w-full px-3 py-2 rounded-lg border text-sm mb-4" style={{ borderColor: COLOR.line, color: COLOR.ink }} />
                <label className="block text-xs font-semibold mb-1" style={{ color: COLOR.ink }}>Anything else? (optional)</label>
                <textarea value={escalateNote} onChange={(e) => setEscalateNote(e.target.value)} placeholder="Any extra context…" rows={3} className="w-full px-3 py-2 rounded-lg border text-sm mb-5" style={{ borderColor: COLOR.line, color: COLOR.ink }} />
                <div className="flex gap-2">
                  <button onClick={() => setShowEscalate(false)} disabled={escalating} className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm border" style={{ borderColor: COLOR.line, color: COLOR.ink2, background: "#fff" }}>Never mind</button>
                  <button onClick={escalate} disabled={escalating || !escalateEmail.includes("@")} className="flex-1 px-4 py-2.5 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: COLOR.ink, color: "#fff" }}>
                    <Mail className="w-4 h-4" />
                    {escalating ? "Sending…" : "Send to support"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .cb-dot {
          display: inline-block; width: 5px; height: 5px; margin: 0 1px;
          background: ${COLOR.muted}; border-radius: 50%;
          animation: cb-typing 1.2s infinite ease-in-out;
        }
        .cb-dot:nth-child(2) { animation-delay: 0.15s; }
        .cb-dot:nth-child(3) { animation-delay: 0.3s; }
        @keyframes cb-typing {
          0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-3px); }
        }
        .cb-pulse {
          animation: cb-pulse-glow 1.4s ease-in-out 0s 3;
        }
        @keyframes cb-pulse-glow {
          0%   { box-shadow: 0 8px 24px rgba(42,30,92,0.25), 0 0 0 0   rgba(245,203,92,0.7); transform: scale(1); }
          50%  { box-shadow: 0 8px 24px rgba(42,30,92,0.25), 0 0 0 14px rgba(245,203,92,0);   transform: scale(1.06); }
          100% { box-shadow: 0 8px 24px rgba(42,30,92,0.25), 0 0 0 0   rgba(245,203,92,0);   transform: scale(1); }
        }
      `}</style>
    </>
  );
}
